/**
 * Human-readable labels and display helpers for templates-v2 enums.
 */

import { BadgeColor } from '@/components/ui/catalyst/badge';
import { TemplateCategory } from '@/services/api/generated/templatesV2Service';

// ---------------------------------------------------------------------------
// Category metadata (rich: color, icon, descriptions)
// ---------------------------------------------------------------------------

interface CategoryMeta {
  label: string;
  shortLabel: string;
  color: BadgeColor;
  icon: 'document' | 'shield' | 'scale' | 'envelope' | 'folder' | 'banknotes';
  description: string;
}

export const CATEGORY_META: Partial<Record<TemplateCategory, CategoryMeta>> = {
  HIPAA_COVERSHEET: {
    label: 'HIPAA Cover Sheet',
    shortLabel: 'Cover',
    color: 'blue',
    icon: 'shield',
    description: 'Medical records request cover pages',
  },
  HIPAA_AUTHORIZATION: {
    label: 'HIPAA Authorization',
    shortLabel: 'Auth',
    color: 'purple',
    icon: 'shield',
    description: 'Patient authorization forms for records release',
  },
  HIPAA_AUTHORIZATION_RETAINER: {
    label: 'HIPAA Authorization (for Retainers)',
    shortLabel: 'HIPAA Ret',
    color: 'purple',
    icon: 'shield',
    description: 'HIPAA authorization bundled with retainer agreements',
  },
  RETAINER: {
    label: 'Retainer Agreement',
    shortLabel: 'Retainer',
    color: 'green',
    icon: 'scale',
    description: 'Client engagement and fee agreements',
  },
  FOIA_LETTER: {
    label: 'FOIA Letter',
    shortLabel: 'FOIA',
    color: 'amber',
    icon: 'envelope',
    description: 'Freedom of Information Act requests',
  },
  LIEN_NOTICE: {
    label: 'Lien Notice',
    shortLabel: 'Lien',
    color: 'rose',
    icon: 'banknotes',
    description: 'Health insurance carrier lien notices',
  },
  CLIENT_COMMUNICATION: {
    label: 'Client Communication',
    shortLabel: 'Client',
    color: 'sky',
    icon: 'envelope',
    description: 'General client communication templates',
  },
  MEDICAL_RECORDS_REQUEST: {
    label: 'Medical Records Request',
    shortLabel: 'Med Req',
    color: 'teal',
    icon: 'document',
    description: 'Requests for medical treatment records',
  },
  AFFIDAVIT: {
    label: 'Affidavit',
    shortLabel: 'Affidavit',
    color: 'indigo',
    icon: 'scale',
    description: 'Sworn statement documents',
  },
  LETTER_OF_PROTECTION: {
    label: 'Letter of Protection',
    shortLabel: 'LOP',
    color: 'emerald',
    icon: 'shield',
    description: 'Letters guaranteeing payment to medical providers',
  },
  PRESERVATION_LETTER: {
    label: 'Preservation Letter',
    shortLabel: 'Preserve',
    color: 'orange',
    icon: 'envelope',
    description: 'Evidence preservation demand letters',
  },
  PIP_DEMAND: {
    label: 'PIP Demand',
    shortLabel: 'PIP',
    color: 'fuchsia',
    icon: 'banknotes',
    description: 'Personal injury protection demand letters',
  },
  FIRM_AUTHORIZATION: {
    label: 'Firm Authorization',
    shortLabel: 'CYA',
    color: 'violet',
    icon: 'scale',
    description: 'Firm authorization letter (CYA) for HIPAA packages',
  },
  OTHER: {
    label: 'Other Document',
    shortLabel: 'Other',
    color: 'zinc',
    icon: 'folder',
    description: 'Miscellaneous document templates',
  },
};

// ---------------------------------------------------------------------------
// Filter options
// ---------------------------------------------------------------------------

export const CATEGORY_FILTER_OPTIONS = [
  { value: 'all', label: 'All categories' },
  { value: 'HIPAA_COVERSHEET', label: 'HIPAA Cover' },
  { value: 'HIPAA_AUTHORIZATION', label: 'HIPAA Auth' },
  { value: 'HIPAA_AUTHORIZATION_RETAINER', label: 'HIPAA Auth (Retainers)' },
  { value: 'FOIA_LETTER', label: 'FOIA' },
  { value: 'LIEN_NOTICE', label: 'Lien Notice' },
  { value: 'CLIENT_COMMUNICATION', label: 'Client Comm' },
  { value: 'MEDICAL_RECORDS_REQUEST', label: 'Med Records Req' },
  { value: 'AFFIDAVIT', label: 'Affidavit' },
  { value: 'LETTER_OF_PROTECTION', label: 'Letter of Protection' },
  { value: 'PRESERVATION_LETTER', label: 'Preservation' },
  { value: 'PIP_DEMAND', label: 'PIP Demand' },
  { value: 'FIRM_AUTHORIZATION', label: 'Firm Auth (CYA)' },
  { value: 'OTHER', label: 'Other' },
] as const;

// ---------------------------------------------------------------------------
// Labels (simple string maps)
// ---------------------------------------------------------------------------

export const TEMPLATE_CATEGORY_LABELS: Record<string, string> = {
  HIPAA_COVERSHEET: 'HIPAA Coversheet',
  HIPAA_AUTHORIZATION: 'HIPAA Authorization',
  HIPAA_AUTHORIZATION_RETAINER: 'HIPAA Authorization (for Retainers)',
  FOIA_LETTER: 'FOIA Letter',
  LIEN_NOTICE: 'Lien Notice',
  RETAINER: 'Retainer',
  LOR_GENERIC: 'LOR (Generic)',
  LOR_FIRST_PARTY: 'LOR (1st Party)',
  LOR_THIRD_PARTY: 'LOR (3rd Party)',
  LOR_HEALTH: 'LOR (Health)',
  WELCOME_LETTER: 'Welcome Letter',
  DROP_LETTER: 'Drop Letter',
  JOINT_VENTURE_NOTICE: 'Joint Venture Notice',
  CLIENT_COMMUNICATION: 'Client Communication',
  MEDICAL_RECORDS_REQUEST: 'Medical Records Request',
  AFFIDAVIT: 'Affidavit',
  LETTER_OF_PROTECTION: 'Letter of Protection',
  PRESERVATION_LETTER: 'Preservation Letter',
  PIP_DEMAND: 'PIP Demand',
  FIRM_AUTHORIZATION: 'Firm Authorization',
  OTHER: 'Other',
};

export const TEMPLATE_FORMAT_LABELS: Record<string, string> = {
  PDF: 'PDF',
  DOCX: 'DOCX',
  HTML: 'HTML',
  SMS: 'SMS',
  EXTERNAL: 'External',
};

export const TEMPLATE_SCOPE_LABELS: Record<string, string> = {
  GLOBAL: 'Global',
  FIRM: 'Firm',
};

export const TEMPLATE_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Active',
  SUPERSEDED: 'Superseded',
  ARCHIVED: 'Archived',
};

export const TEMPLATE_STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
  SUPERSEDED: 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
  ARCHIVED: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-500/10 dark:text-zinc-400',
};

export const TEMPLATE_FORMAT_COLORS: Record<string, string> = {
  PDF: 'bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400',
  DOCX: 'bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400',
  HTML: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-500/10 dark:text-fuchsia-400',
  SMS: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
  EXTERNAL: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-500/10 dark:text-zinc-400',
};

export const TEMPLATE_SCOPE_COLORS: Record<string, string> = {
  GLOBAL: 'bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400',
  FIRM: 'bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-400',
};

/**
 * Synthetic option value used by the unified Firm filter to represent
 * globally-scoped templates (scope=GLOBAL).
 */
export const GLOBAL_FIRM_FILTER_VALUE = '__GLOBAL__';

export const CONTEXT_CATEGORY_LABELS: Record<string, string> = {
  firm: 'Firm',
  matter: 'Matter',
  attorney: 'Attorney',
  client: 'Client',
  police: 'Police Report',
  medical: 'Medical Provider',
  insurance: 'Insurance (General)',
  insurance1p: '1st Party Insurance',
  insurance3p: '3rd Party Insurance',
  insuranceHealth: 'Health Insurance',
  incident: 'Incident',
  treatmentRequest: 'Treatment Request',
  treatmentChannel: 'Treatment Channel',
  transmission: 'Transmission',
  recipient: 'Recipient',
};

export const CATEGORY_OPTIONS = Object.entries(TEMPLATE_CATEGORY_LABELS).map(
  ([value, label]) => ({ value, label }),
);

export const FORMAT_OPTIONS = Object.entries(TEMPLATE_FORMAT_LABELS).map(
  ([value, label]) => ({ value, label }),
);

export const SCOPE_OPTIONS = Object.entries(TEMPLATE_SCOPE_LABELS).map(
  ([value, label]) => ({ value, label }),
);

export const STATUS_OPTIONS = Object.entries(TEMPLATE_STATUS_LABELS).map(
  ([value, label]) => ({ value, label }),
);
